import React, { useEffect, useState, useRef } from 'react';
import { DownloadIcon, UsersIcon, PackageIcon, DollarSignIcon, CheckCircleIcon, ChevronDownIcon, SearchIcon, ClockIcon } from 'lucide-react';
import { ResponsiveContainer, LineChart as RechartsLineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip } from 'recharts';
import { MetricCard } from '../components/MetricCard';
import { LineChart } from '../components/charts/LineChart';
import { DateSelector } from '../components/DateSelector';
import { ViewAllButton } from '../components/ViewAllButton';
interface Service {
  id: string;
  name: string;
  category: string;
  isAll?: boolean;
}
export const ServicePerformance: React.FC = () => {
  const availableServices: Service[] = [{
    id: '1',
    name: 'Vehicle Tax Renewal',
    category: 'Transportation'
  }, {
    id: '2',
    name: 'Passport Application',
    category: 'Immigration'
  }, {
    id: '3',
    name: 'Driving License Renewal',
    category: 'Transportation'
  }, {
    id: '4',
    name: 'Birth Certificate',
    category: 'Civil Registry'
  }, {
    id: '5',
    name: 'Marriage Certificate',
    category: 'Civil Registry'
  }, {
    id: '6',
    name: 'Business Registration',
    category: 'Business'
  }, {
    id: '7',
    name: 'Property Tax Payment',
    category: 'Taxation'
  }, {
    id: '8',
    name: 'Building Permit',
    category: 'Construction'
  }];
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedService, setSelectedService] = useState<Service>({
    id: 'all',
    name: 'All Services',
    category: 'All',
    isAll: true
  });
  const [isServiceDropdownOpen, setIsServiceDropdownOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const timeRanges = ['12 months', '3 months', '30 days', '7 days', 'Today'];
  const servicePerformanceData = Array.from({
    length: 12
  }, (_, i) => ({
    month: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][i],
    applications: 400 + Math.random() * 400,
    processed: 350 + Math.random() * 300,
    completed: 300 + Math.random() * 200
  }));
  const topServicesByVolume = [{
    name: 'Vehicle Tax Renewal',
    volume: 1245789
  }, {
    name: 'Passport Application',
    volume: 985642
  }, {
    name: 'Driving License Renewal',
    volume: 854126
  }, {
    name: 'Birth Certificate',
    volume: 712456
  }, {
    name: 'Marriage Certificate',
    volume: 458741
  }];
  const topDepartments = [{
    name: 'Market',
    volume: 2654789
  }, {
    name: 'Land',
    volume: 1845632
  }, {
    name: 'Health',
    volume: 1568974
  }, {
    name: 'Education',
    volume: 987456
  }, {
    name: 'Civil Registry',
    volume: 587412
  }];
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedZone, setSelectedZone] = useState('all');
  const [selectedWereda, setSelectedWereda] = useState('all');
  const [selectedKebele, setSelectedKebele] = useState('all');
  const [selectedAgency, setSelectedAgency] = useState('all');
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node) && !buttonRef.current?.contains(event.target as Node)) {
        setIsDatePickerOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };
  const handlePreviousMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setSelectedDate(newDate);
  };
  const handleNextMonth = () => {
    const newDate = new Date(selectedDate);
    newDate.setMonth(newDate.getMonth() + 1);
    setSelectedDate(newDate);
  };
  const handleDateChange = () => {
    setIsDatePickerOpen(!isDatePickerOpen);
  };
  const handleViewAllServices = () => {
    console.log('View all services clicked');
    alert('View all services functionality would be implemented here');
  };
  const handleViewAllDepartments = () => {
    console.log('View all departments clicked');
    alert('View all departments functionality would be implemented here');
  };
  const DatePicker = () => {
    if (!isDatePickerOpen) return null;
    return <div ref={datePickerRef} className="absolute mt-2 bg-white rounded-lg shadow-lg border border-gray-200 p-4 w-64 z-10" data-id="element-425">
        <div className="flex items-center justify-between mb-4" data-id="element-426">
          <button onClick={handlePreviousMonth} className="p-1 hover:bg-gray-100 rounded-full" aria-label="Previous month" data-id="element-427">
            <ChevronLeftIcon size={20} data-id="element-428" />
          </button>
          <span className="font-medium" data-id="element-429">{formatDate(selectedDate)}</span>
          <button onClick={handleNextMonth} className="p-1 hover:bg-gray-100 rounded-full" aria-label="Next month" data-id="element-430">
            <ChevronRightIcon size={20} data-id="element-431" />
          </button>
        </div>
        <div className="grid grid-cols-3 gap-2" data-id="element-432">
          {Array.from({
          length: 12
        }, (_, i) => {
          const date = new Date(selectedDate.getFullYear(), i, 1);
          return <button key={i} onClick={() => {
            setSelectedDate(date);
            setIsDatePickerOpen(false);
          }} className={`py-2 px-3 text-sm rounded-md ${selectedDate.getMonth() === i ? 'bg-blue-50 text-blue-600' : 'hover:bg-gray-100'}`} data-id="element-433">
                  {date.toLocaleString('default', {
              month: 'short'
            })}
                </button>;
        })}
        </div>
        <div className="mt-4 grid grid-cols-2 gap-2" data-id="element-434">
          <button onClick={() => {
          const newDate = new Date(selectedDate);
          newDate.setFullYear(newDate.getFullYear() - 1);
          setSelectedDate(newDate);
        }} className="py-2 px-3 text-sm rounded-md hover:bg-gray-100" data-id="element-435">
            {selectedDate.getFullYear() - 1}
          </button>
          <button onClick={() => {
          const newDate = new Date(selectedDate);
          newDate.setFullYear(newDate.getFullYear() + 1);
          setSelectedDate(newDate);
        }} className="py-2 px-3 text-sm rounded-md hover:bg-gray-100" data-id="element-436">
            {selectedDate.getFullYear() + 1}
          </button>
        </div>
      </div>;
  };
  const filteredServices = availableServices.filter(service => service.name.toLowerCase().includes(searchQuery.toLowerCase()) || service.category.toLowerCase().includes(searchQuery.toLowerCase()));
  return <div data-id="element-437">
      <div className="mb-6" data-id="element-438">
        <h2 className="text-xl font-semibold text-gray-800" data-id="element-439">
          Service Performance
        </h2>
        <div className="relative w-full mt-2" ref={dropdownRef} data-id="element-440">
          <div className="flex items-center space-x-4 flex-wrap gap-y-2 mt-4" data-id="element-441">
            <div className="relative" data-id="element-442">
              <select className="appearance-none bg-white border border-gray-200 rounded-md pl-3 pr-8 py-2 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" value={selectedService.id} onChange={e => {
              if (e.target.value === 'all') {
                setSelectedService({
                  id: 'all',
                  name: 'All Services',
                  category: 'All',
                  isAll: true
                });
              } else {
                const service = availableServices.find(s => s.id === e.target.value);
                if (service) {
                  setSelectedService(service);
                }
              }
            }} data-id="element-443">
                <option value="all" data-id="element-444">All Services</option>
                {availableServices.map(service => <option key={service.id} value={service.id} data-id="element-445">
                    {service.name}
                  </option>)}
              </select>
              <ChevronDownIcon size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-446" />
            </div>
            <div className="relative" data-id="element-447">
              <select className="appearance-none bg-white border border-gray-200 rounded-md pl-3 pr-8 py-2 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" value={selectedAgency} onChange={e => setSelectedAgency(e.target.value)} data-id="element-448">
                <option value="all" data-id="element-449">All Agencies</option>
                <option value="transportation" data-id="element-450">Transportation</option>
                <option value="immigration" data-id="element-451">Immigration</option>
                <option value="civil" data-id="element-452">Civil Registry</option>
                <option value="business" data-id="element-453">Business</option>
              </select>
              <ChevronDownIcon size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-454" />
            </div>
            <div className="relative" data-id="element-455">
              <select className="appearance-none bg-white border border-gray-200 rounded-md pl-3 pr-8 py-2 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" value={selectedRegion} onChange={e => setSelectedRegion(e.target.value)} data-id="element-456">
                <option value="all" data-id="element-457">All Regions</option>
                <option value="amhara" data-id="element-458">Amhara</option>
                <option value="oromia" data-id="element-459">Oromia</option>
                <option value="tigray" data-id="element-460">Tigray</option>
                <option value="snnpr" data-id="element-461">SNNPR</option>
              </select>
              <ChevronDownIcon size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-462" />
            </div>
            <div className="relative" data-id="element-463">
              <select className="appearance-none bg-white border border-gray-200 rounded-md pl-3 pr-8 py-2 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" value={selectedZone} onChange={e => setSelectedZone(e.target.value)} data-id="element-464">
                <option value="all" data-id="element-465">All Zones</option>
                <option value="north" data-id="element-466">North Zone</option>
                <option value="south" data-id="element-467">South Zone</option>
                <option value="east" data-id="element-468">East Zone</option>
                <option value="west" data-id="element-469">West Zone</option>
              </select>
              <ChevronDownIcon size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-470" />
            </div>
            <div className="relative" data-id="element-471">
              <select className="appearance-none bg-white border border-gray-200 rounded-md pl-3 pr-8 py-2 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" value={selectedWereda} onChange={e => setSelectedWereda(e.target.value)} data-id="element-472">
                <option value="all" data-id="element-473">All Weredas</option>
                <option value="wereda1" data-id="element-474">Wereda 01</option>
                <option value="wereda2" data-id="element-475">Wereda 02</option>
                <option value="wereda3" data-id="element-476">Wereda 03</option>
                <option value="wereda4" data-id="element-477">Wereda 04</option>
              </select>
              <ChevronDownIcon size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-478" />
            </div>
            <div className="relative" data-id="element-479">
              <select className="appearance-none bg-white border border-gray-200 rounded-md pl-3 pr-8 py-2 text-sm text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500" value={selectedKebele} onChange={e => setSelectedKebele(e.target.value)} data-id="element-480">
                <option value="all" data-id="element-481">All Kebeles</option>
                <option value="kebele1" data-id="element-482">Kebele 01</option>
                <option value="kebele2" data-id="element-483">Kebele 02</option>
                <option value="kebele3" data-id="element-484">Kebele 03</option>
                <option value="kebele4" data-id="element-485">Kebele 04</option>
              </select>
              <ChevronDownIcon size={16} className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" data-id="element-486" />
            </div>
            <div className="flex items-center space-x-2 ml-auto" data-id="element-487">
              <button onClick={() => {
              setSelectedService({
                id: 'all',
                name: 'All Services',
                category: 'All',
                isAll: true
              });
              setSelectedAgency('all');
              setSelectedRegion('all');
              setSelectedZone('all');
              setSelectedWereda('all');
              setSelectedKebele('all');
            }} className="px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors duration-200" data-id="element-488">
                Clear Filters
              </button>
              <button onClick={() => {
              // Add your apply filters logic here
              console.log('Applying filters');
            }} className="px-4 py-2 text-sm text-white bg-blue-500 rounded-md hover:bg-blue-600 transition-colors duration-200" data-id="element-489">
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      </div>
      <div className="mb-6" data-id="element-490">
        <div className="flex space-x-4 mb-6" data-id="element-491">
          {timeRanges.map(range => <button key={range} className={`px-4 py-2 text-sm rounded-md ${range === '30 days' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-492">
              {range}
            </button>)}
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8" data-id="element-493">
        <MetricCard title="Total Live Services" value="387" icon={<PackageIcon size={20} data-id="element-495" />} change={{
        value: '+12',
        positive: true,
        prefix: '+'
      }} footer={<a href="#" className="text-blue-500 text-sm hover:underline" data-id="element-496">
              Download report
            </a>} data-id="element-494" />
        <MetricCard title="Applications Processed" value="124,584" icon={<CheckCircleIcon size={20} data-id="element-498" />} change={{
        value: '+5.3%',
        positive: true
      }} footer={<a href="#" className="text-blue-500 text-sm hover:underline" data-id="element-499">
              Download report
            </a>} data-id="element-497" />
        <MetricCard title="Total Amount Processed" value="$28.5M" icon={<DollarSignIcon size={20} data-id="element-501" />} change={{
        value: '+3.7%',
        positive: true
      }} footer={<a href="#" className="text-blue-500 text-sm hover:underline" data-id="element-502">
              Download report
            </a>} data-id="element-500" />
        <MetricCard title="Completion Rate" value="91.42%" icon={<UsersIcon size={20} data-id="element-504" />} change={{
        value: '-1.2%',
        positive: false
      }} footer={<a href="#" className="text-blue-500 text-sm hover:underline" data-id="element-505">
              Download report
            </a>} data-id="element-503" />
        <MetricCard title="SLA Compliance" value="94.8%" icon={<ClockIcon size={20} data-id="element-507" />} change={{
        value: '+2.3%',
        positive: true
      }} footer={<a href="#" className="text-blue-500 text-sm hover:underline" data-id="element-508">
              Download report
            </a>} data-id="element-506" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-id="element-509">
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-510">
          <div className="flex justify-between items-center mb-6" data-id="element-511">
            <h3 className="text-lg font-medium" data-id="element-512">
              Top Services by Application Volume
            </h3>
            <div className="flex items-center" data-id="element-513">
              <DateSelector selectedDate={selectedDate} onDateChange={setSelectedDate} data-id="element-514" />
              <ViewAllButton onClick={handleViewAllServices} data-id="element-515" />
            </div>
          </div>
          {topServicesByVolume.map(service => <div key={service.name} className="mb-4 last:mb-0" data-id="element-516">
              <div className="flex justify-between items-center mb-1" data-id="element-517">
                <span className="text-sm font-medium" data-id="element-518">{service.name}</span>
                <span className="text-sm font-medium" data-id="element-519">
                  {service.volume.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-520">
                <div className="bg-blue-500 h-2.5 rounded-full" style={{
              width: `${service.volume / topServicesByVolume[0].volume * 100}%`
            }} data-id="element-521"></div>
              </div>
            </div>)}
        </div>
        <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-522">
          <div className="flex justify-between items-center mb-6" data-id="element-523">
            <h3 className="text-lg font-medium" data-id="element-524">Top Performing Departments</h3>
            <div className="flex items-center" data-id="element-525">
              <DateSelector selectedDate={selectedDate} onDateChange={setSelectedDate} data-id="element-526" />
              <ViewAllButton onClick={handleViewAllDepartments} data-id="element-527" />
            </div>
          </div>
          {topDepartments.map(dept => <div key={dept.name} className="mb-4 last:mb-0" data-id="element-528">
              <div className="flex justify-between items-center mb-1" data-id="element-529">
                <span className="text-sm font-medium" data-id="element-530">{dept.name}</span>
                <span className="text-sm font-medium" data-id="element-531">
                  {dept.volume.toLocaleString()}
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2.5" data-id="element-532">
                <div className="bg-blue-500 h-2.5 rounded-full" style={{
              width: `${dept.volume / topDepartments[0].volume * 100}%`
            }} data-id="element-533"></div>
              </div>
            </div>)}
        </div>
      </div>
      <div className="bg-white rounded-lg shadow-sm p-6" data-id="element-534">
        <div className="flex justify-between items-center mb-6" data-id="element-535">
          <div data-id="element-536">
            <h3 className="text-lg font-medium" data-id="element-537">Service Performance Trends</h3>
            <p className="text-sm text-gray-500" data-id="element-538">
              Application submission and processing data
            </p>
          </div>
          <button className="text-blue-600 bg-white border border-gray-200 px-4 py-1.5 rounded-md text-sm" data-id="element-539">
            View report
          </button>
        </div>
        <div className="mb-4" data-id="element-540">
          <div className="flex space-x-4" data-id="element-541">
            {timeRanges.map(range => <button key={range} className={`px-4 py-1.5 text-sm rounded-md ${range === '12 months' ? 'bg-blue-50 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-542">
                {range}
              </button>)}
          </div>
        </div>
        <div className="relative" data-id="element-543">
          <ResponsiveLineChart data={servicePerformanceData} data-id="element-544" />
          <div className="absolute top-4 right-4 flex items-center space-x-4" data-id="element-545">
            <div className="flex items-center" data-id="element-546">
              <div className="w-3 h-3 rounded-full bg-blue-500 mr-2" data-id="element-547"></div>
              <span className="text-xs text-gray-600" data-id="element-548">Applications</span>
            </div>
            <div className="flex items-center" data-id="element-549">
              <div className="w-3 h-3 rounded-full bg-orange-500 mr-2" data-id="element-550"></div>
              <span className="text-xs text-gray-600" data-id="element-551">Processed</span>
            </div>
            <div className="flex items-center" data-id="element-552">
              <div className="w-3 h-3 rounded-full bg-green-500 mr-2" data-id="element-553"></div>
              <span className="text-xs text-gray-600" data-id="element-554">Completed</span>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
const ResponsiveLineChart = ({
  data
}) => {
  return <div className="h-80" data-id="element-555">
      <ResponsiveContainer width="100%" height="100%" data-id="element-556">
        <RechartsLineChart data={data} margin={{
        top: 10,
        right: 10,
        left: 0,
        bottom: 20
      }} data-id="element-557">
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" data-id="element-558" />
          <XAxis dataKey="month" tick={{
          fontSize: 12,
          fill: '#64748b'
        }} tickLine={false} axisLine={{
          stroke: '#e2e8f0'
        }} data-id="element-559" />
          <YAxis tick={{
          fontSize: 12,
          fill: '#64748b'
        }} tickLine={false} axisLine={{
          stroke: '#e2e8f0'
        }} data-id="element-560" />
          <Tooltip data-id="element-561" />
          <Line type="monotone" dataKey="applications" stroke="#2563eb" strokeWidth={2} dot={false} activeDot={{
          r: 6,
          stroke: 'white',
          strokeWidth: 2
        }} data-id="element-562" />
          <Line type="monotone" dataKey="processed" stroke="#f97316" strokeWidth={2} dot={false} activeDot={{
          r: 6,
          stroke: 'white',
          strokeWidth: 2
        }} data-id="element-563" />
          <Line type="monotone" dataKey="completed" stroke="#22c55e" strokeWidth={2} dot={false} activeDot={{
          r: 6,
          stroke: 'white',
          strokeWidth: 2
        }} data-id="element-564" />
        </RechartsLineChart>
      </ResponsiveContainer>
    </div>;
};